import { useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Label } from '@/shared/components/ui/label';
import { MultiSelectCombobox } from '@/shared/components/ui/multi-select-combobox';
import {
  SpecialtyCoverageEditor,
  type SpecialtyCoverageItem,
} from '@/shared/components/specialty-coverage';
import type { ProviderEditorFormData } from '../schemas';
import { useCategories, useSpecialties } from '../../../hooks/useTaxonomy';

type SpecialtiesCoverageSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  /** Server messages like specialties[0].coverageStates: ... */
  serverFieldErrors?: string[];
};

function parseSpecialtyFieldErrors(
  messages: string[],
  specialties: SpecialtyCoverageItem[],
): Record<string, string> {
  const byId: Record<string, string> = {};
  for (const message of messages) {
    const match = message.match(/specialties\[(\d+)\](?:\.\S+)?:\s*(.*)/i);
    if (!match) continue;
    const index = Number(match[1]);
    const specialty = specialties[index];
    if (!specialty) continue;
    byId[specialty.specialtyId] = match[2] || message;
  }
  return byId;
}

/** Admin Specialties & Coverage — categories auto-derived; expertise stays in Taxonomies. */
export function SpecialtiesCoverageSection({
  control,
  errors,
  serverFieldErrors = [],
}: SpecialtiesCoverageSectionProps) {
  const { setValue, getValues } = useFormContext<ProviderEditorFormData>();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: specialtyOptions = [], isLoading: specialtiesLoading } = useSpecialties();

  const specialties = useWatch({ control, name: 'specialties', defaultValue: [] }) ?? [];

  const fieldErrors = useMemo(
    () => parseSpecialtyFieldErrors(serverFieldErrors, specialties),
    [serverFieldErrors, specialties],
  );

  useEffect(() => {
    if (specialtyOptions.length === 0 || specialties.length === 0) return;

    const categoryIdsFromSpecialties = Array.from(
      new Set(
        specialties
          .map((item) => specialtyOptions.find((opt) => opt.id === item.specialtyId)?.categoryId)
          .filter((id): id is string => id != null),
      ),
    );

    const currentCategoryIds = getValues('categoryIds') || [];
    const sortedNew = [...categoryIdsFromSpecialties].sort();
    const sortedCurrent = [...currentCategoryIds].sort();
    const hasChanged =
      sortedNew.length !== sortedCurrent.length ||
      sortedNew.some((id, index) => id !== sortedCurrent[index]);

    if (hasChanged) {
      setValue('categoryIds', categoryIdsFromSpecialties, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [specialties, specialtyOptions, setValue, getValues]);

  return (
    <div className="space-y-8">
      <Controller
        name="specialties"
        control={control}
        render={({ field }) => (
          <SpecialtyCoverageEditor
            value={field.value ?? []}
            onChange={field.onChange}
            specialtyOptions={specialtyOptions}
            specialtyOptionsLoading={specialtiesLoading}
            permissions={{ canManageAttachments: true }}
            fieldErrors={fieldErrors}
          />
        )}
      />
      {errors.specialties?.message ? (
        <p className="text-sm text-red-600" role="alert">
          {errors.specialties.message}
        </p>
      ) : null}

      <div>
        <Label htmlFor="categoryIds" className="text-sm font-medium">
          Categories (Auto-selected)
        </Label>
        <p className="mb-2 mt-1 text-sm text-gray-500">
          These categories are automatically selected based on your chosen specialties
        </p>
        <Controller
          name="categoryIds"
          control={control}
          render={({ field }) => (
            <MultiSelectCombobox
              options={categories}
              value={field.value || []}
              onChange={field.onChange}
              placeholder="Categories will auto-populate based on specialties"
              searchPlaceholder="Search categories..."
              emptyText="No categories found"
              loading={categoriesLoading}
              error={errors.categoryIds?.message}
              disabled={true}
            />
          )}
        />
      </div>
    </div>
  );
}
